const INLINE_DASH_MARKER = /(?:^|\s+)[\u2212\u2013\u2014-]\s+/g;
const INLINE_BULLET_MARKER = /(?:^|\s+)•\s+/g;
const INLINE_NUMBER_MARKER = /(?:^|\s+)([1-9]\d*)\.\s+/g;
const BOOK_INDENT_ATTRIBUTE = /\bdata-book-indent\s*=\s*(["'])([0-8])\1/i;

const readBookIndentAttribute = (html = '') => {
  const match = String(html).match(BOOK_INDENT_ATTRIBUTE);
  return match ? ` data-book-indent="${match[2]}"` : '';
};

const collectMarkerMatches = (value, markerPattern) => {
  const source = String(value || '');
  return {
    source,
    matches: [...source.matchAll(markerPattern)],
  };
};

const splitInlineMarkerItems = (value, markerPattern) => {
  const { source, matches } = collectMarkerMatches(value, markerPattern);

  if (matches.length === 0) {
    return null;
  }

  const intro = source.slice(0, matches[0].index).trim();
  const items = matches
    .map((match, index) => {
      const start = match.index + match[0].length;
      const end = matches[index + 1]?.index ?? source.length;
      return source.slice(start, end).trim();
    })
    .filter(Boolean);

  return items.length ? { intro, items } : null;
};

const renderInlineDashList = (value, indentAttribute = '') => {
  const result = splitInlineMarkerItems(value, INLINE_DASH_MARKER);

  if (!result) {
    return null;
  }

  const introHtml = result.intro
    ? `<p${indentAttribute}>${result.intro}</p>`
    : '';
  const listHtml = `<ul class="book-inline-dash-list">${result.items
    .map((item) => `<li${indentAttribute}>${item}</li>`)
    .join('')}</ul>`;

  return `${introHtml}${listHtml}`;
};

const renderInlineBulletList = (value, indentAttribute = '') => {
  const result = splitInlineMarkerItems(value, INLINE_BULLET_MARKER);

  if (!result) {
    return null;
  }

  const introHtml = result.intro
    ? `<p${indentAttribute}>${result.intro}</p>`
    : '';
  const listHtml = `<ul class="book-inline-bullet-list">${result.items
    .map((item) => `<li${indentAttribute}>${item}</li>`)
    .join('')}</ul>`;

  return `${introHtml}${listHtml}`;
};

const splitInlineNumberItems = (value) => {
  const { source, matches } = collectMarkerMatches(
    value,
    INLINE_NUMBER_MARKER
  );

  if (matches.length < 2) {
    return null;
  }

  const intro = source.slice(0, matches[0].index).trim();
  const groups = [];
  let currentGroup = [];

  matches.forEach((match, index) => {
    const number = Number(match[1]);
    const start = match.index + match[0].length;
    const end = matches[index + 1]?.index ?? source.length;
    const text = source.slice(start, end).trim();

    if (number === 1 && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    if (text) {
      currentGroup.push({ number, text });
    }
  });

  if (currentGroup.length) {
    groups.push(currentGroup);
  }

  return groups.length ? { intro, groups } : null;
};

const renderInlineNumberLists = (value, indentAttribute = '') => {
  const result = splitInlineNumberItems(value);

  if (!result) {
    return null;
  }

  const introHtml = result.intro
    ? `<p${indentAttribute}>${result.intro}</p>`
    : '';
  const listsHtml = result.groups
    .map(
      (group) =>
        `<ol class="book-inline-number-list">${group
          .map(
            ({ number, text }) =>
              `<li${indentAttribute} value="${number}">${text}</li>`
          )
          .join('')}</ol>`
    )
    .join('');

  return `${introHtml}${listsHtml}`;
};

const renderInlineListBlocks = (value, indentAttribute = '') =>
  renderInlineBulletList(value, indentAttribute) ||
  renderInlineNumberLists(value, indentAttribute) ||
  renderInlineDashList(value, indentAttribute);

const normalizeInlineListParagraphs = (html) => {
  const semanticLists = [];
  const protectedHtml = html.replace(
    /<(ul|ol)\b[^>]*>[\s\S]*?<\/\1>/gi,
    (block) => {
      const index = semanticLists.length;
      semanticLists.push(block);
      return `__SEMANTIC_LIST_${index}__`;
    }
  );

  const normalized = protectedHtml.replace(
    /<p\b[^>]*>([^<>]*)<\/p>/gi,
    (paragraph, text) =>
      renderInlineListBlocks(
        text,
        readBookIndentAttribute(paragraph)
      ) || paragraph
  );

  return normalized.replace(
    /__SEMANTIC_LIST_(\d+)__/g,
    (placeholder, indexValue) =>
      semanticLists[Number(indexValue)] || placeholder
  );
};

export const convertLegacyTagsToHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  let html = text;
  const calloutClass = (type) => {
    const base =
      'callout my-3 rounded-lg border px-4 py-3 text-lg font-semibold text-white [&_p]:text-white [&_li]:text-white [&_strong]:text-white [&_em]:text-white [&_u]:text-white';
    const variants = {
      note: 'border-blue-700 bg-blue-600',
      warning: 'border-red-700 bg-red-600',
      reminder: 'border-amber-700 bg-amber-600',
      tip: 'border-green-700 bg-green-600',
    };
    return `${base} ${variants[type] || variants.note}`;
  };

  html = html.replace(
    /<(note|warning|reminder|tip)\b[^>]*>([\s\S]*?)<\/(note|warning|reminder|tip)>/gi,
    (match, openTag, inner) => {
      const type = String(openTag || 'note').toLowerCase();
      return `<div data-callout="true" data-type="${type}" class="${calloutClass(type)}">${inner}</div>`;
    }
  );

  const prefixRegex =
    /^\s*(?:~\s*)?(note|warning|reminder|tip)\s*[:\-~]\s*/i;
  const stripHtml = (value) =>
    String(value || '').replace(/<[^>]*>/g, '').trim();
  const calloutBlocks = [];
  html = html.replace(
    /<div\b[^>]*\bdata-callout\b[^>]*>[\s\S]*?<\/div>/gi,
    (block) => {
      const index = calloutBlocks.length;
      calloutBlocks.push(block);
      return `__CALLOUT_BLOCK_${index}__`;
    }
  );

  if (/<\/?[a-z][\s\S]*>/i.test(html)) {
    html = html.replace(/<p\b[^>]*>[\s\S]*?<\/p>/gi, (paragraph) => {
      const inner = paragraph
        .replace(/^<p\b[^>]*>/i, '')
        .replace(/<\/p>$/i, '');
      const match = stripHtml(inner).match(prefixRegex);
      if (!match) return paragraph;
      const type = String(match[1] || 'note').toLowerCase();
      return `<div data-callout="true" data-type="${type}" class="${calloutClass(type)}">${paragraph}</div>`;
    });
  } else {
    html = String(html)
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(prefixRegex);
        if (!match) return renderInlineListBlocks(line) || `<p>${line}</p>`;
        const type = String(match[1] || 'note').toLowerCase();
        return `<div data-callout="true" data-type="${type}" class="${calloutClass(type)}"><p>${line}</p></div>`;
      })
      .join('');
  }

  html = html.replace(/__CALLOUT_BLOCK_(\d+)__/g, (match, indexValue) => {
    const index = Number(indexValue);
    return calloutBlocks[index] || match;
  });

  const replacements = [
    [/<grasiu>(.*?)<\/grasiu>/gi, '<strong><em><u>$1</u></em></strong>'],
    [/<gruis>(.*?)<\/gruis>/gi, '<strong><u><em>$1</em></u></strong>'],
    [/<igras>(.*?)<\/igras>/gi, '<em><strong><u>$1</u></strong></em>'],
    [/<iugras>(.*?)<\/iugras>/gi, '<em><u><strong>$1</strong></u></em>'],
    [/<ugrasi>(.*?)<\/ugrasi>/gi, '<u><strong><em>$1</em></strong></u>'],
    [/<ugris>(.*?)<\/ugris>/gi, '<u><strong><em>$1</em></strong></u>'],
    [/<uigras>(.*?)<\/uigras>/gi, '<u><em><strong>$1</strong></em></u>'],
    [/<grasi>(.*?)<\/grasi>/gi, '<strong><em>$1</em></strong>'],
    [/<grasu>(.*?)<\/grasu>/gi, '<strong><u>$1</u></strong>'],
    [/<iugras>(.*?)<\/iugras>/gi, '<em><u>$1</u></em>'],
    [/<gras>(.*?)<\/gras>/gi, '<strong>$1</strong>'],
    [/<i>(.*?)<\/i>/gi, '<em>$1</em>'],
  ];
  replacements.forEach(([pattern, replacement]) => {
    html = html.replace(pattern, replacement);
  });
  return normalizeInlineListParagraphs(html);
};
